// apps/web/src/pages/compras/conferencia-item-linha.tsx — Fase 13, Plano 22 (COMP-03, EST-10, EST-12, D-08, D-10, D-15, D-16).
// Linha da grade de conferência + linha expandida. Essencial na linha: Item da nota, Matéria-prima, Quantidade a receber, Alertas
// (sem rolagem horizontal); o restante vai para a expandida, aberta automaticamente quando há alerta. Tudo é estado LOCAL: nada
// é gravado aqui. Cor nunca é o único sinal: todo alerta tem ícone e texto.
import { Fragment, useState } from 'react';
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleDollarSign,
  Link2Off,
  MoreHorizontal,
  PencilLine,
  Scale,
  TrendingUp,
  Undo2,
  type LucideIcon,
} from 'lucide-react';
import type { ItemConferencia } from '@pieltec/contracts';
import { Button } from '../../components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { LocalizacaoSelect } from '../../components/estoque/localizacao-select';
import {
  alertasDoItem,
  custoDaEntrada,
  equivalentePedido,
  excedenteDoItem,
  fisicaDoItem,
  foraDoPedido,
  formatarCustoUnitario,
  pedidoEfetivo,
  quantidadeCorrigida,
  type CodigoAlertaLinha,
  type EstadoConferencia,
  type ItemConferenciaEstado,
} from '../../lib/compras/conferencia';
import { disponivelParaEstorno } from '../../lib/compras/estorno';
import { formatarMoeda, formatarQuantidade } from '../../lib/vendas/formatacao';
import { mensagemNumeroDigitado } from '../../lib/vendas/numero';
import { CampoSelect } from '../../components/ui/campo-select';

interface ApresentacaoAlerta {
  readonly texto: string;
  readonly Icone: LucideIcon;
  readonly classes: string;
}

const MENSAGEM_FISICA_INVALIDA = 'Informe uma quantidade maior que zero, com até 4 casas.';

const ROSE = 'bg-rose-50 text-rose-700 border-rose-200';
const AMBER = 'bg-amber-50 text-amber-700 border-amber-200';

const ALERTAS: Record<CodigoAlertaLinha, ApresentacaoAlerta> = {
  SEM_VINCULO: { texto: 'Sem vínculo · bloqueia entrada', Icone: Link2Off, classes: ROSE },
  SEM_FATOR: { texto: 'Sem fator · bloqueia entrada', Icone: Scale, classes: ROSE },
  UNIDADE_DIVERGENTE: { texto: 'Unidade da nota diverge do cadastro', Icone: Scale, classes: AMBER },
  FORA_PEDIDO: { texto: 'Fora do pedido', Icone: TrendingUp, classes: AMBER },
  EXCEDENTE: { texto: 'Acima do pedido', Icone: TrendingUp, classes: AMBER },
  QUANTIDADE_CORRIGIDA: { texto: 'Quantidade corrigida', Icone: PencilLine, classes: AMBER },
};

export interface ConferenciaItemLinhaProps {
  readonly estado: EstadoConferencia;
  readonly item: ItemConferenciaEstado;
  /** Modo leitura (nota já recebida): a linha mostra o que entrou, com o selo "Recebido". */
  readonly somenteLeitura: boolean;
  readonly recebido: ItemConferencia['recebido'];
  readonly podeCadastrarMaterial: boolean;
  /** Erro do servidor posicionado neste item. */
  readonly erro: string | null;
  readonly aoAtualizar: (parcial: Partial<ItemConferenciaEstado>) => void;
  readonly aoDigitarFisica: (texto: string) => void;
  readonly aoVincular: () => void;
  readonly aoCadastrar: () => void;
  readonly aoDefinirFator: () => void;
  /** Ações pós-recebimento (só com compras:gerenciar); ausente = sem menu "⋯". A rede fica nos diálogos, não aqui. */
  readonly acoesRecebido?: { readonly aoEstornar: () => void; readonly aoDevolver?: () => void } | null;
}

function quantidadeComUnidade(valor: number | null, unidade: string | null): string {
  return valor === null ? '—' : `${formatarQuantidade(valor)}${unidade ? ` ${unidade}` : ''}`;
}

export function ConferenciaItemLinha(props: ConferenciaItemLinhaProps) {
  const { estado, item, somenteLeitura, recebido, erro, acoesRecebido } = props;
  const disponivelEstorno = recebido ? disponivelParaEstorno(recebido) : 0;
  const estornado = recebido ? recebido.estornadoFisico > 0 : false;
  const alertas = alertasDoItem(estado, item);
  const [aberturaManual, setAberturaManual] = useState<boolean | null>(null);
  // Item com alerta (ou com erro do servidor) fica expandido por padrão para que nada bloqueante passe despercebido.
  const aberto = !somenteLeitura && (aberturaManual ?? (alertas.length > 0 || erro !== null));
  const idDetalhe = `detalhe-item-${item.notaCompraItemId}`;
  const unidadeEstoque = item.material?.unidadeEstoque ?? null;
  const fisica = fisicaDoItem(item);

  return (
    <Fragment>
      <tr className="border-t align-top">
        <td className="px-3 py-2">
          <div className="flex items-start gap-1">
            {!somenteLeitura && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-9 shrink-0"
                aria-expanded={aberto}
                aria-controls={idDetalhe}
                aria-label={`${aberto ? 'Recolher' : 'Expandir'} item ${item.numeroItem}`}
                onClick={() => setAberturaManual(!aberto)}
              >
                {aberto ? <ChevronDown aria-hidden="true" /> : <ChevronRight aria-hidden="true" />}
              </Button>
            )}
            <div>
              <div className="font-medium">
                {item.numeroItem}. {item.codigoProduto}
              </div>
              <div className="text-xs text-muted-foreground">
                {item.descricao} · {formatarQuantidade(item.quantidadeComercial)} {item.unidadeComercial}
              </div>
            </div>
          </div>
        </td>
        <td className="px-3 py-2">
          <CelulaMaterial {...props} />
        </td>
        <td className="px-3 py-2 text-right tabular-nums">
          {somenteLeitura ? (
            <span>{recebido ? quantidadeComUnidade(recebido.quantidadeEstoqueFisica, unidadeEstoque) : '—'}</span>
          ) : (
            <span>{quantidadeComUnidade(fisica, unidadeEstoque)}</span>
          )}
          {somenteLeitura && recebido && <div className="text-xs text-muted-foreground">em {recebido.localizacaoNome}</div>}
        </td>
        <td className="px-3 py-2">
          {somenteLeitura ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                <CheckCircle2 className="size-3.5 shrink-0" aria-hidden="true" />
                Recebido
              </span>
              {estornado && (
                <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                  <Undo2 className="size-3.5 shrink-0" aria-hidden="true" />
                  {disponivelEstorno > 0 ? 'Estornado em parte' : 'Estornado'}
                  {recebido ? ` · ${formatarQuantidade(recebido.estornadoFisico)}${unidadeEstoque ? ` ${unidadeEstoque}` : ''}` : ''}
                </span>
              )}
              {acoesRecebido && recebido && disponivelEstorno > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button type="button" variant="ghost" size="icon" aria-label={`Mais ações do item ${item.numeroItem}`} className="size-11 sm:size-9">
                      <MoreHorizontal aria-hidden="true" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onSelect={acoesRecebido.aoEstornar} className="min-h-10">
                      Estornar entrada
                    </DropdownMenuItem>
                    {acoesRecebido.aoDevolver && (
                      <DropdownMenuItem onSelect={acoesRecebido.aoDevolver} className="min-h-10">
                        Devolver ao fornecedor
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          ) : (
            <ul className="flex flex-col items-start gap-1">
              {alertas.map((codigo) => {
                const { texto, Icone, classes } = ALERTAS[codigo];
                return (
                  <li key={codigo} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${classes}`}>
                    <Icone className="size-3.5 shrink-0" aria-hidden="true" />
                    {texto}
                  </li>
                );
              })}
            </ul>
          )}
        </td>
      </tr>
      {aberto && (
        <tr id={idDetalhe} className="bg-slate-50">
          <td colSpan={4} className="px-5 py-4">
            <DetalheItem {...props} />
          </td>
        </tr>
      )}
    </Fragment>
  );
}

function CelulaMaterial({ item, somenteLeitura, podeCadastrarMaterial, aoVincular, aoCadastrar, aoDefinirFator }: ConferenciaItemLinhaProps) {
  const semMaterial = item.material === null;
  const semFator = !semMaterial && item.fator === null;
  return (
    <div className="flex flex-col gap-2">
      {item.material ? (
        <span>
          <span className="font-medium">{item.material.codigo}</span> — {item.material.nome}
        </span>
      ) : somenteLeitura ? (
        <span className="text-muted-foreground">—</span>
      ) : (
        (
          <div className="flex flex-col gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-rose-800">
            <p className="flex items-center gap-2 text-sm">
              <Link2Off className="size-4 shrink-0" aria-hidden="true" />
              Matéria-prima não vinculada — este item não dá entrada.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" size="sm" onClick={aoVincular}>
                Vincular matéria-prima
              </Button>
              {podeCadastrarMaterial && (
                <Button type="button" variant="outline" size="sm" onClick={aoCadastrar}>
                  Cadastrar matéria-prima
                </Button>
              )}
            </div>
          </div>
        )
      )}
      {semFator && !somenteLeitura && (
        <div className="flex flex-col gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-rose-800">
          <p className="flex items-center gap-2 text-sm">
            <Scale className="size-4 shrink-0" aria-hidden="true" />
            Sem fator de conversão — este item não dá entrada.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" variant="outline" size="sm" onClick={aoDefinirFator}>
              Definir fator
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

const CAMPO = 'ui-control min-h-[var(--control-height,2rem)] w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800';

function Campo({ rotulo, htmlFor, children }: { rotulo: string; htmlFor?: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      {htmlFor ? (
        <Label htmlFor={htmlFor} className="text-xs font-medium text-slate-600">
          {rotulo}
        </Label>
      ) : (
        <span className="text-xs font-medium text-slate-600">{rotulo}</span>
      )}
      {children}
    </div>
  );
}

function DetalheItem({ estado, item, erro, aoAtualizar, aoDigitarFisica }: ConferenciaItemLinhaProps) {
  const unidadeEstoque = item.material?.unidadeEstoque ?? '';
  const pedido = pedidoEfetivo(estado, item);
  const fisica = fisicaDoItem(item);
  const equivalente = pedido ? equivalentePedido(fisica, pedido.fatorPedidoParaEstoqueSnapshot) : null;
  const custo = custoDaEntrada(item.valorProduto, fisica);
  const corrigida = quantidadeCorrigida(item);
  const excedente = excedenteDoItem(estado, item);
  const fora = foraDoPedido(estado, item);
  const idBase = item.notaCompraItemId;
  const seletorPedido = item.usarPedido && item.pedidoItem ? 'pedido' : 'sem';

  return (
    <div className="flex flex-col gap-4">
      {erro && (
        <div role="alert" className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {erro}
        </div>
      )}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Campo rotulo="Item do pedido" htmlFor={`pedido-item-${idBase}`}>
          <CampoSelect
            id={`pedido-item-${idBase}`}
            value={seletorPedido}
            disabled={estado.semPedido}
            onValueChange={(valor) => aoAtualizar({ usarPedido: valor === 'pedido' })}
            options={[
              ...(item.pedidoItem
                ? [{ value: 'pedido', label: `${item.pedidoItem.pedidoNumero} · item ${item.pedidoItem.numeroItem} (saldo ${formatarQuantidade(item.pedidoItem.saldoAReceber)} ${item.pedidoItem.unidadePedido})` }]
                : []),
              { value: 'sem', label: 'Sem pedido' },
            ]}
          />
        </Campo>

        <Campo rotulo="Quantidade declarada na NF">
          <span className="tabular-nums">
            {formatarQuantidade(item.quantidadeComercial)} {item.unidadeComercial}
          </span>
        </Campo>

        <Campo rotulo="Fator">
          <span className="tabular-nums">
            {item.fator === null ? 'Sem fator' : `1 ${item.unidadeComercial} = ${formatarQuantidade(item.fator)} ${unidadeEstoque}`}
          </span>
        </Campo>

        <Campo rotulo="Conversão esperada">
          <span className="tabular-nums">{quantidadeComUnidade(item.esperada, unidadeEstoque)}</span>
        </Campo>

        <Campo rotulo={`Quantidade física recebida (${unidadeEstoque || 'un'})`} htmlFor={`fisica-${idBase}`}>
          <input
            id={`fisica-${idBase}`}
            inputMode="decimal"
            value={item.fisicaTexto}
            disabled={item.fator === null}
            onChange={(e) => aoDigitarFisica(e.target.value)}
            aria-invalid={item.fator !== null && fisica === null}
            className={`${CAMPO} text-right tabular-nums`}
          />
          {item.fator !== null && fisica === null && <span className="text-xs text-rose-700">{mensagemNumeroDigitado(item.fisicaTexto, MENSAGEM_FISICA_INVALIDA)}</span>}
        </Campo>

        <Campo rotulo="Equivalente do pedido">
          <span className="tabular-nums">{pedido && equivalente !== null ? quantidadeComUnidade(equivalente, pedido.unidadePedido) : '—'}</span>
        </Campo>

        <Campo rotulo="Custo da entrada">
          <span className="tabular-nums font-medium">{custo === null ? '—' : `${formatarCustoUnitario(custo)}/${unidadeEstoque}`}</span>
          <span className="text-xs text-muted-foreground">Valor do item na NF ÷ quantidade física recebida</span>
          <span className="text-xs text-muted-foreground tabular-nums">Valor do item: {formatarMoeda(item.valorProduto)}</span>
        </Campo>

        <Campo rotulo="Receber em" htmlFor={`local-${idBase}`}>
          <LocalizacaoSelect id={`local-${idBase}`} value={item.localizacaoId} onChange={(localizacaoId) => aoAtualizar({ localizacaoId })} rotulo="Receber em" />
        </Campo>

        <Campo rotulo="Custo médio atual">
          <span className="inline-flex items-center gap-1 text-muted-foreground tabular-nums">
            {item.custoMedioAtual === null ? (
              <>
                <CircleDollarSign className="size-4 shrink-0" aria-hidden="true" />
                Sem custo médio
              </>
            ) : (
              `${formatarCustoUnitario(item.custoMedioAtual)}/${unidadeEstoque}`
            )}
          </span>
        </Campo>
      </div>

      {corrigida && (
        <div className="flex flex-col gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <p className="flex items-center gap-2 font-medium">
            <PencilLine className="size-4 shrink-0" aria-hidden="true" />
            Quantidade corrigida
          </p>
          <p className="tabular-nums">
            Declarado na NF: {formatarQuantidade(item.quantidadeComercial)} {item.unidadeComercial}. Conversão esperada: {quantidadeComUnidade(item.esperada, unidadeEstoque)}. Físico recebido:{' '}
            {quantidadeComUnidade(fisica, unidadeEstoque)}.
          </p>
          <Label htmlFor={`just-correcao-${idBase}`}>Justificativa da correção (obrigatória)</Label>
          <Textarea
            id={`just-correcao-${idBase}`}
            value={item.justificativaCorrecao}
            maxLength={500}
            rows={2}
            onChange={(e) => aoAtualizar({ justificativaCorrecao: e.target.value })}
          />
        </div>
      )}

      {excedente && (
        <div className="flex flex-col gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <p className="flex items-center gap-2">
            <TrendingUp className="size-4 shrink-0" aria-hidden="true" />
            {excedente.itemDaJustificativa === item.notaCompraItemId
              ? `Quantidade acima do saldo do pedido (+${formatarQuantidade(excedente.excedente)} ${excedente.unidadePedido}). Informe a justificativa para continuar.`
              : `Quantidade acima do saldo do pedido (+${formatarQuantidade(excedente.excedente)} ${excedente.unidadePedido}). A justificativa é informada no outro item que usa o mesmo item do pedido.`}
          </p>
          {excedente.itemDaJustificativa === item.notaCompraItemId && (
            <>
              <Label htmlFor={`just-excedente-${idBase}`}>Justificativa (obrigatória)</Label>
              <Textarea
                id={`just-excedente-${idBase}`}
                value={item.justificativaExcedente}
                maxLength={500}
                rows={2}
                onChange={(e) => aoAtualizar({ justificativaExcedente: e.target.value })}
              />
            </>
          )}
        </div>
      )}

      {fora && (
        <div className="flex flex-col gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <p className="flex items-center gap-2">
            <TrendingUp className="size-4 shrink-0" aria-hidden="true" />
            Item da nota fora do pedido. Informe a justificativa para continuar.
          </p>
          <Label htmlFor={`just-fora-${idBase}`}>Justificativa (obrigatória)</Label>
          <Textarea
            id={`just-fora-${idBase}`}
            value={item.justificativaForaPedido}
            maxLength={500}
            rows={2}
            onChange={(e) => aoAtualizar({ justificativaForaPedido: e.target.value })}
          />
        </div>
      )}
    </div>
  );
}
